import React, { useEffect, useState } from 'react';

import car from 'asset/car.png';
import cn from 'classnames';
import { motion } from 'framer-motion';
import { useInView } from 'react-intersection-observer';

import { data_tech_steps } from './content';

export default function StepsWithMiddleLine({ id }) {
  const [activeCircle, setActiveCircle] = useState(0);
  const [previousActiveCircle, setPreviousActiveCircle] = useState(-1);
  const [carPath, setCarPath] = useState(0);

  const handleInViewChange = (isInView, index) => {
    if (isInView) {
      // not sure, but suppose to prevent setting activeCircle to lower value than prev
      if (previousActiveCircle < 0 && index + 1 < activeCircle) return;
      // do not return if finish has been reached
      if (activeCircle === data_tech_steps.length) return;
      // remove if in case of reverse movement
      if (index + 1 > previousActiveCircle) {
        setPreviousActiveCircle(activeCircle);
        setActiveCircle(index + 1);
      }
    }
  };

  function moveCar() {
    const selectedCircle = document.getElementById(`circle-${activeCircle}`);
    if (!selectedCircle) return;

    const car = document.getElementById(`car`);

    const carSize = car.clientHeight;
    if (!carSize) return;
    // padding of item + items size
    const itemRow = document.getElementById(`path-row`);
    const blockSize = itemRow.clientHeight; // 13.5rem

    setCarPath(blockSize * activeCircle - carSize);
  }

  useEffect(() => {
      moveCar();

      window.addEventListener('resize', moveCar);

      return () => {
        window.removeEventListener('resize', moveCar);
      }
  }, [activeCircle]);

  //before:top-[12.5rem]
  //before:h-[calc(100%-13.5rem)]

  /*
    top: padding + 1/2 of circle's height - to be sure that we start drawing line on center of first circle
    top = 6rem (96px) + 7.5rem / 2
    height: 100% of container - padding bottom - (circle height / 2)- magic number 1rem (idk where it comes from)
    height = 100%-8.75rem-(7.5rem/2)-1rem
  */
  const middleLine2 =
    'absolute left-[50%] top-[calc(6rem+(7.5rem/2))] sm:top-[calc(3rem+34px)] w-[1px] h-[calc(100%-8.75rem-(7.5rem/2)-1rem)] lg:left-[calc(75px/2)] lg:h-[calc(100%-132px)] lg:top-11 sm:left-5 bg-gray-100';

  return (
    <section id={id} className="gl-wrapper gl-wrapper-y relative bg-black pb-[8.75rem]">
      <p className="gl-heading-3 mt-2 text-demo-xr-blue">try iT out </p>
      <h2 className="gl-heading-2 text-white">Sit down. Put on the headset. Configure away.</h2>

      <div className={cn('relative mx-auto flex max-w-[61.875rem] flex-col overflow-visible pt-0 lg:pt-8 sm:pt-0')}>
        <span className={middleLine2} />
        <div
          data-easteregg={carPath && `It was a long journey, we were tired, but we traveled ${carPath} km.`}
          style={{ top: carPath, transitionDuration: '1500ms' }}
          className={cn(
            'absolute left-[calc(50%-5.25rem)] top-[20px] z-[100] w-[10.125rem] transition-[top] delay-700 lg:!hidden',
          )}
        >
          <img
            id="car"
            className="w-[10.125rem] drop-shadow-[2px_4px_6px_rgba(0,0,0,1)]"
            src={car}
            fetchPriority="high"
            alt="A car which moves to every item when you scrolling page"
          />
        </div>
        {data_tech_steps.map((item, idx) => {
          const { ref, inView } = useInView({
            triggerOnce: true,
            onChange: inView => handleInViewChange(inView, idx),
            threshold: 1,
            rootMargin: '0px 0px -150px 0px',
          });
          return (
            <div
              id="path-row"
              key={item.title}
              className="item-wrapper relative flex min-h-[12.1875rem] w-full justify-start pt-11 even:justify-end lg:min-h-[unset] lg:pt-8 sm:pt-8"
            >
              <motion.div
                id={`circle-${idx + 1}`}
                ref={ref}
                viewport={{ once: true }}
                transition={{ duration: 1, delay: 0 }}
                initial={{ scale: 0 }}
                animate={{ scale: inView ? 1 : 0 }}
                className={cn(
                  'circle absolute left-[calc(50%-3.75rem)] top-[45%] z-10 flex aspect-square w-[7.5rem] items-center justify-center rounded-full border border-demo-xr-blue bg-black transition-[background-color] duration-700 min-2xl:top-[55%] lg:left-0 lg:top-8 lg:w-[75px] lg:!scale-100 sm:top-8 sm:flex sm:aspect-auto sm:h-fit sm:w-8 sm:items-start sm:!rounded-none sm:!border-none',
                  activeCircle === idx + 1 && '!bg-demo-xr-blue lg:!bg-black',
                )}
              >
                <span className="stroaked min-w-[calc(1.5rem+1px)] py-2 font-Proxima text-[2.5rem] leading-[0.85] text-white min-lg:hidden">
                  {idx + 1}
                </span>
              </motion.div>
              <div className="item-block-wrapper flex min-h-[7.5rem] min-w-[400px] items-center lg:min-h-[unset] lg:w-full lg:min-w-[unset] lg:items-start">
                <motion.div
                  viewport={{ once: true }}
                  initial={{ borderColor: '#000' }}
                  animate={{ borderColor: inView ? '#646262' : '#000' }}
                  transition={{ duration: 0.1, delay: 1.5 }}
                  className={`item-block relative z-10 flex w-full max-w-[25rem] gap-2.5 overflow-hidden border border-gray-80 bg-black p-2.5 lg:ml-[120px] lg:max-w-full lg:!border-gray-80 sm:static sm:ml-9 ${idx % 2 === 0 ? 'left' : 'right'}`}
                >
                  <motion.span
                    viewport={{ once: true }}
                    initial={{ x: '-80px' }}
                    animate={{ x: inView ? 0 : '-80px' }}
                    transition={{ duration: 1, delay: 0.5 }}
                    className="block-number font-Proxima text-subtitle font-black leading-none text-demo-xr-blue lg:hidden lg:!transform-none"
                  >
                    {idx + 1}
                  </motion.span>
                  <div className="flex flex-col gap-2">
                    <motion.h3
                      viewport={{ once: true }}
                      initial={{ y: '-125%' }}
                      animate={{ y: inView ? 0 : '-125%' }}
                      transition={{ duration: 1, delay: 0.5 }}
                      className="font-Proxima text-b-sm font-black uppercase text-demo-xr-blue lg:!transform-none"
                    >
                      {item.title}
                    </motion.h3>
                    <motion.p
                      transition={{ duration: 1, delay: 0.5 }}
                      viewport={{ once: true }}
                      initial={{ y: '125%' }}
                      animate={{ y: inView ? 0 : '125%' }}
                      className={cn(
                        'font-OpenSans font-medium text-white lg:!transform-none',
                        idx === data_tech_steps.length - 1 && '!font-bold',
                      )}
                    >
                      {item.description}
                    </motion.p>
                  </div>
                </motion.div>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}
